import { useState, useCallback } from 'react';
import { useAuth } from '../../../app/providers';
import { Button, ModalWrapper } from '../../../shared/components/ui';
import { useDashboardOverview, useRecentActivity } from '../hooks';
import { InviteHomeManagerModal } from '@/features/houses/components/modals';
import { AddHouseForm } from '@/features/houses';
// import { PaymentForm } from '@/features/payment_test/PaymentForm';

const DashboardPage = () => {
  const { user } = useAuth();

  // Fetch dashboard data
  const { data: dashboardData, isLoading, error } = useDashboardOverview();

  // Modal state management
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isAddHouseModalOpen, setIsAddHouseModalOpen] = useState(false);

  // Transform API data to match component structure
  const statsData = dashboardData
    ? [
        {
          label: 'Total Properties',
          value: dashboardData.stats.totalProperties,
          icon: 'assets/icons/property.svg',
        },
        {
          label: 'Total Beds',
          value: dashboardData.stats.totalBeds,
          icon: 'assets/icons/home.svg',
        },
        {
          label: 'Bed Utilization Rate',
          value: dashboardData.stats.bedUtilization,
          icon: 'assets/icons/home-resident.svg',
        },
        {
          label: 'Monthly Income',
          value: `$${(
            dashboardData.stats.monthlyIncome
          ).toLocaleString()}`,
          icon: 'assets/icons/income.svg',
        },
      ]
    : [];

  const housesData = dashboardData
    ? dashboardData.houses.map((house) => ({
        id: house.id,
        name: house.name,
        capacity: house.capacityPercentage,
        thumbnail: house.images[0] || '/assets/images/houses/house-1.png',
        status: house.status,
      }))
    : [];

  // Fetch recent activity
  const { data: recentActivityData, isLoading: isActivityLoading } =
    useRecentActivity();

  const recentActivityTabs = [
    'Latest payments',
    'Overdue program fees',
    'New applications',
    'Maintenance request',
  ];

  const activityByTab = {
    'Latest payments': recentActivityData?.latestPayments || [],
    'Overdue program fees': recentActivityData?.overdueInvoices || [],
    'New applications': recentActivityData?.newApplications || [],
    'Maintenance request': recentActivityData?.maintenanceRequests || [],
  };

  const [activeTab, setActiveTab] = useState(recentActivityTabs[0]);

  // Modal handlers
  const handleOpenInviteModal = useCallback(() => {
    setIsInviteModalOpen(true);
  }, []);

  const handleOpenAddHouseModal = useCallback(() => {
    setIsAddHouseModalOpen(true);
  }, []);

  const handleCloseInviteModal = useCallback(() => {
    setIsInviteModalOpen(false);
  }, []);

  const handleCloseAddHouseModal = useCallback(() => {
    setIsAddHouseModalOpen(false);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Main Content */}
      <main className="max-w-7xl mx-auto">
        {/* Header Section */}
        {user?.role === 'OWNER' && (
          <>
            {/* Invite Manager CTA  */}
            <div className="w-full flex flex-col lg:flex-row items-center justify-between gap-4 md:px-4 lg:px-8 mb-6">
              <div className="text-center lg:text-start w-full lg:w-[80%]">
                <h3 className="text-lg md:text-2xl font-bold mb-2 text-text-primary">
                  Invite a manager to your house
                </h3>
                <p className="text-gray-600 lg:mb-8 text-base md:text-xl text-text-secondary">
                  Add a trusted house manager to help oversee rentals, payments,
                  and resident communications. You can remove access anytime.
                </p>
              </div>
              <Button
                variant="primary"
                size="lg"
                onClick={handleOpenInviteModal}
              >
                Invite house manager
              </Button>
            </div>
          </>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-gray-600">Loading dashboard...</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">
              Failed to load dashboard data. Please try again later.
            </p>
          </div>
        )}

        {/* Main Content Area */}
        {!isLoading &&
          !error &&
          (statsData && statsData[0].value === 0 ? (
            /* Empty State */
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 text-center">
              {/* Empty State - Add House Section */}
              <div className="mt-4 md:mt-20">
                <div className="w-[155px] mx-auto mb-6">
                  <img
                    src="/assets/images/empty-state-home.png"
                    alt="empty-state-home"
                    className="w-full h-full"
                  />
                </div>

                <div className="mb-6">
                  <h3 className="text-xl md:text-2xl font-bold mb-2 text-text-primary">
                    Setup your first house
                  </h3>
                  <p className="text-gray-600 mb-8 text-base md:text-xl lg:w-md mx-auto text-text-primary">
                    Get started by adding house details for quick management.
                  </p>
                </div>

                <Button
                  variant="primary"
                  size="lg"
                  onClick={handleOpenAddHouseModal}
                >
                  Add house
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* Stats Overview  */}
              <section className="mb-6">
                <p className="mb-4 text-xl font-bold text-text-primary">
                  Overview
                </p>
                {/* stats  */}
                <div>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-10 ">
                    {statsData.map((item, index) => (
                      <div key={index} className="p-2 md:p-4 card">
                        <img
                          src={item.icon}
                          alt={item.label}
                          className="mb-2 md:mb-6"
                        />
                        <h4 className="text-lg md:text-3xl font-bold mb-2 text-text-primary">
                          {item.value}
                        </h4>
                        <p className="text-base md:text-xl text-text-secondary">
                          {item.label}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              {/* Houses  */}
              <section className="mb-6">
                <p className="mb-4 text-lg md:text-xl font-bold text-text-primary">
                  Houses
                </p>
                {/* Houses Row */}
                <div className="grid grid-col-1 md:grid-cols-2 xl:grid-cols-4 gap-6 lg:gap-4 ">
                  {housesData.map((item, index) => (
                    <div key={index} className="card relative">
                      <div className="mb-4 h-50 w-full overflow-hidden rounded-2xl relative">
                        <img
                          src={item.thumbnail}
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                        <div
                          className="absolute inset-0"
                          style={{
                            background:
                              'linear-gradient(180deg, rgba(0, 0, 0, 0.32) 0%, rgba(0, 0, 0, 0) 100%)',
                          }}
                        ></div>
                      </div>
                      <h4 className="text-lg md:text-xl font-bold mb-2 text-text-primary">
                        {item.name}
                      </h4>
                      <div className="text-text-secondary flex items-center justify-between">
                        <span>Capacity</span>
                        <span className="text-text-primary">
                          {item.capacity}
                        </span>
                      </div>

                      {/* Status badge */}
                      <div className="absolute top-2 right-2">
                        <Button
                          variant={
                            item.status === 'ACTIVE' ? 'success' : 'secondary'
                          }
                        >
                          {item.status === 'ACTIVE' ? 'Active' : 'Inactive'}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/*  Recent Activity - Show when there are houses */}
              <section className="mb-6">
                <p className="mb-4 text-lg md:text-xl font-bold text-text-primary">
                  Recent activity
                </p>

                <div className="">
                  {/* Mobile Dropdown - Visible only on small screens */}
                  <div className="md:hidden mb-4">
                    <select
                      value={activeTab}
                      onChange={(e) => setActiveTab(e.target.value)}
                      className="w-full p-3 rounded-lg border border-gray-200 text-text-primary bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20fill%3D%22none%22%20viewBox%3D%220%200%2024%2024%22%20stroke%3D%22currentColor%22%3E%3Cpath%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20stroke-width%3D%222%22%20d%3D%22M19%209l-7%207-7-7%22%2F%3E%3C%2Fsvg%3E')] bg-[length:1.25rem_1.25rem] bg-[right_0.75rem_center] bg-no-repeat"
                    >
                      {recentActivityTabs.map((tab) => (
                        <option key={tab} value={tab}>
                          {tab}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Desktop Tabs - Visible only on md screens and above */}
                  <div className="hidden md:flex items-center gap-4 mb-4 overflow-x-auto scrollbar-hide">
                    {recentActivityTabs.map((tab) => (
                      <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`px-4 py-2 cursor-pointer transition-all duration-300 whitespace-nowrap !inline-flex !min-w-[200px] !w-[200px] lg:w-auto rounded-lg border-[1px] ${
                          activeTab === tab
                            ? 'font-bold text-text-primary border-gray-400'
                            : 'text-text-secondary border-gray-200'
                        }`}
                      >
                        {tab}
                      </button>
                    ))}
                  </div>

                  {/* Backup of previous implementation
                  <div className="flex items-center gap-4 mb-4 overflow-x-auto scrollbar-hide">
                    {recentActivityTabs.map((tab) => (
                      <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`px-4 py-2 cursor-pointer transition-all duration-300 whitespace-nowrap !inline-flex !min-w-[200px] !w-[200px] lg:w-auto rounded-lg border-[1px] ${
                          activeTab === tab
                            ? 'font-bold text-text-primary border-gray-400'
                            : 'text-text-secondary border-gray-200'
                        }`}
                      >
                        {tab}
                      </button>
                    ))}
                  </div>
                  */}

                  {/* Table */}
                  <div className="w-full overflow-x-auto scrollbar-hide mb-4">
                    {isActivityLoading ? (
                      <div className="flex justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                      </div>
                    ) : (
                      <>
                        {/* Header row - Dynamic based on tab */}
                        <div className="grid grid-cols-5 min-w-[700px]">
                          {activeTab === 'Maintenance request' ? (
                            <>
                              <div className="text-text-primary text-center py-4 font-bold">
                                Title
                              </div>
                              <div className="text-center text-text-primary py-4 font-bold">
                                Category
                              </div>
                              <div className="text-center text-text-primary py-4 font-bold">
                                Priority
                              </div>
                              <div className="text-center text-text-primary py-4 font-bold">
                                Status
                              </div>
                              <div className="text-center text-text-primary py-4 font-bold">
                                Date
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="text-text-primary text-center py-4 font-bold">
                                Resident name
                              </div>
                              <div className="text-center text-text-primary py-4 font-bold">
                                Amount
                              </div>
                              <div className="text-center text-text-primary py-4 font-bold">
                                Bed
                              </div>
                              <div className="text-center text-text-primary py-4 font-bold">
                                Status
                              </div>
                              <div className="text-center text-text-primary py-4 font-bold">
                                Payment date
                              </div>
                            </>
                          )}
                        </div>

                        {/* Data rows */}
                        {(
                          activityByTab[
                            activeTab as keyof typeof activityByTab
                          ] || []
                        ).length === 0 ? (
                          <div className="text-center py-8 text-gray-500 col-span-5">
                            No recent activity found
                          </div>
                        ) : (
                          (
                            activityByTab[
                              activeTab as keyof typeof activityByTab
                            ] || []
                          ).map((row, i) => (
                            <div
                              key={i}
                              className="grid grid-cols-5 min-w-[700px] border-t border-gray-100"
                            >
                              {activeTab === 'Maintenance request' ? (
                                // Maintenance request columns
                                <>
                                  <div className="text-text-primary text-start p-4">
                                    {row.title || row.name}
                                  </div>
                                  <div className="text-center text-text-primary p-4">
                                    {row.category || 'Maintenance'}
                                  </div>
                                  <div
                                    className={`text-center p-4 ${
                                      row.priority === 'HIGH'
                                        ? 'text-red-600 font-semibold'
                                        : row.priority === 'MEDIUM'
                                        ? 'text-yellow-600'
                                        : 'text-gray-600'
                                    }`}
                                  >
                                    {row.priority || 'LOW'}
                                  </div>
                                  <div className="text-center text-text-primary p-4">
                                    {row.status}
                                  </div>
                                  <div className="text-center text-text-primary p-4">
                                    {row.date}
                                  </div>
                                </>
                              ) : (
                                // Payment-related columns
                                <>
                                  <div className="text-text-primary text-center p-4">
                                    {row.name}
                                  </div>
                                  <div className="text-end text-text-primary p-4">
                                    {row.amount}
                                  </div>
                                  <div className="text-center text-text-primary p-4">
                                    {row.bed}
                                  </div>
                                  <div
                                    className={`text-center p-4 ${
                                      row.status === 'Paid'
                                        ? 'text-[#177D3D]'
                                        : row.status === 'Overdue'
                                        ? 'text-[#C0392B]'
                                        : 'text-text-secondary'
                                    }`}
                                  >
                                    {row.status}
                                  </div>
                                  <div className="text-center text-text-primary p-4">
                                    {row.date}
                                  </div>
                                </>
                              )}
                            </div>
                          ))
                        )}
                      </>
                    )}
                  </div>
                </div>
              </section>
            </>
          ))}
      </main>

      <InviteHomeManagerModal
        isOpen={isInviteModalOpen}
        onClose={handleCloseInviteModal}
        house={null}
      />

      {/* Add House Modal */}
      <ModalWrapper
        isOpen={isAddHouseModalOpen}
        onClose={handleCloseAddHouseModal}
      >
        <div className="flex items-center justify-center w-full min-h-full">
          <AddHouseForm onClose={handleCloseAddHouseModal} />
        </div>
      </ModalWrapper>
    </div>
  );
};

export default DashboardPage;
